import { InternalRequest, type ClientConfig, type TokenStorage } from './client.js';
import { authEndpoints } from './endpoints/auth.js';
import { devicesEndpoints } from './endpoints/devices.js';
import { positionsEndpoints } from './endpoints/positions.js';
import { rulesEndpoints } from './endpoints/rules.js';
import { chatEndpoints } from './endpoints/chat.js';
import { reportsEndpoints } from './endpoints/reports.js';
import { brandingEndpoints } from './endpoints/branding.js';
import { usersEndpoints } from './endpoints/users.js';
import { orgsEndpoints } from './endpoints/orgs.js';
import { storageEndpoints } from './endpoints/storage.js';
import {
  staffMyWorkEndpoints,
  staffShiftsEndpoints,
  staffAttendanceEndpoints,
  staffTrackingEndpoints,
  staffPositionsEndpoints,
  staffMyAlertsEndpoints,
  staffMyRulesEndpoints,
  staffMyAccountEndpoints,
  staffMyVisitsEndpoints,
  staffMyScorecardEndpoints,
} from './endpoints/staff.js';
import { safetyEndpoints } from './endpoints/safety.js';
import { workEndpoints } from './endpoints/work.js';
import { formsEndpoints } from './endpoints/forms.js';
import { formSubmissionsEndpoints } from './endpoints/form-submissions.js';
import { formRequirementsEndpoints } from './endpoints/form-requirements.js';
import { snapEndpoints } from './endpoints/snap.js';
import { layersEndpoints } from './endpoints/layers.js';
import { poisEndpoints } from './endpoints/pois.js';
import { geofencesEndpoints } from './endpoints/geofences.js';
import { riskmizeEndpoints } from './endpoints/riskmize.js';
import { broadcastsEndpoints } from './endpoints/broadcasts.js';
import { mustersEndpoints } from './endpoints/musters.js';
import { incidentsEndpoints } from './endpoints/incidents.js';
import { assureEndpoints } from './endpoints/assure.js';
import { fuelEndpoints } from './endpoints/fuel.js';

export type { FuelOverviewRow, FuelOverviewResponse, FuelOverviewStatus } from './endpoints/fuel.js';

export type { TokenStorage, ClientConfig, RequestOptions } from './client.js';
export type {
  MyWorkResponse,
  LocationBody,
  PunchBody,
  ClockInBody,
  ClockOutBody,
  BreakBody,
  AttendanceRecord,
  AttendanceToday,
  AttendanceCurrentResponse,
  AttendancePhotoResponse,
  AttendancePunchResponse,
  AttendancePhotoErrorCode,
  StaffPositionFix,
  StaffPositionsIngestRequest,
  StaffPositionsIngestResponse,
  StaffLatestPosition,
  StaffAlertRow,
  StaffAlertDetail,
  StaffAlertsResponse,
  StaffRuleRow,
  StaffRulesResponse,
  PoiVisitRow,
  StaffScorecard,
} from './endpoints/staff.js';
export type { Poi } from './endpoints/pois.js';
export type { Geofence } from './endpoints/geofences.js';
export {
  ATTENDANCE_PHOTO_CAPABILITY,
  STOP_LOCATION_CAPABILITY,
  SOS_LOCATION_CAPABILITY,
  CAPABILITIES_HEADER,
  ATTENDANCE_PHOTO_ERROR_CODES,
} from './endpoints/staff.js';
export type { SosLocationBody } from './endpoints/safety.js';
export type { RNFileLike, FileRecord } from './endpoints/storage.js';
export type { RiskIncidentFeature, RiskIncidentCollection } from './endpoints/riskmize.js';
export type { LayersStatus, TileTokenResponse } from './endpoints/layers.js';
export type {
  AudienceSpec as BroadcastAudienceSpec,
  BroadcastChannel,
  BroadcastChannelStatus,
  BroadcastEstimate,
  BroadcastCapDecision,
  BroadcastPreviewResponse,
  BroadcastSendResponse,
  BroadcastRow,
  BroadcastListResponse,
  BroadcastReceiptRow,
  BroadcastDetailResponse,
} from './endpoints/broadcasts.js';
export type {
  MusterRosterSpec,
  MusterZoneSpec,
  MusterState,
  MusterReason,
  MusterSource,
  MusterSnapshot,
  MusterCounts,
  MusterSummary,
  MusterRosterPerson,
  MusterPreview,
  MusterDeclareResponse,
  MusterListItem,
  MusterListResponse,
  MusterBoardPerson,
  MusterBoard,
  MusterMineView,
  MusterEventItem,
  MusterEventsResponse,
  MusterNotificationReceipt,
  MusterNotifications,
  MusterPersonState,
} from './endpoints/musters.js';
export type {
  IncidentSeverity,
  IncidentStatus,
  IncidentSourceType,
  IncidentSummary,
  IncidentListItem,
  IncidentListResponse,
  IncidentEvent,
  IncidentDetail,
  IncidentEventsResponse,
  IncidentNotificationReceipt,
  IncidentNotificationsResponse,
  IncidentMineView,
  IncidentRaiseRequest,
  IncidentRaiseResponse,
  IncidentPatchRequest,
} from './endpoints/incidents.js';
export { ApiError } from '@trackizy/types';

export function createClient(config: ClientConfig) {
  const req = new InternalRequest(config);
  return {
    request: req.request.bind(req),
    setBaseUrl: req.setBaseUrl.bind(req),
    getBaseUrl: req.getBaseUrl.bind(req),
    auth: authEndpoints(req),
    devices: devicesEndpoints(req),
    positions: positionsEndpoints(req),
    rules: rulesEndpoints(req),
    chat: chatEndpoints(req),
    reports: reportsEndpoints(req),
    branding: brandingEndpoints(req),
    users: usersEndpoints(req),
    orgs: orgsEndpoints(req),
    storage: storageEndpoints(req),
    staffMyWork: staffMyWorkEndpoints(req),
    staffShifts: staffShiftsEndpoints(req),
    staffAttendance: staffAttendanceEndpoints(req),
    staffTracking: staffTrackingEndpoints(req),
    staffPositions: staffPositionsEndpoints(req),
    staffMyAlerts: staffMyAlertsEndpoints(req),
    pois: poisEndpoints(req),
    geofences: geofencesEndpoints(req),
    riskmize: riskmizeEndpoints(req),
    staffMyVisits: staffMyVisitsEndpoints(req),
    staffMyScorecard: staffMyScorecardEndpoints(req),
    staffMyRules: staffMyRulesEndpoints(req),
    staffMyAccount: staffMyAccountEndpoints(req),
    safety: safetyEndpoints(req),
    work: workEndpoints(req),
    forms: formsEndpoints(req),
    formSubmissions: formSubmissionsEndpoints(req),
    formRequirements: formRequirementsEndpoints(req),
    snap: snapEndpoints(req),
    layers: layersEndpoints(req),
    broadcasts: broadcastsEndpoints(req),
    musters: mustersEndpoints(req),
    incidents: incidentsEndpoints(req),
    assure: assureEndpoints(req),
    fuel: fuelEndpoints(req),
  };
}

export type TrackizyClient = ReturnType<typeof createClient>;
